import { ToasterService } from '@abp/ng.theme.shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, DestroyRef, inject, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  DEFAULT_TOASTER_LIFE,
  ModalBuilder,
  StepperSuccessModal,
  ZnStepperWizard,
  ZnWizardHost,
} from '@zendra/shared';
import { finalize as finalizeObservable } from 'rxjs';
import type { ISelectStandsToDivideStepData } from './interfaces/ISelectStandsToDivideStepData';
import {
  StandDivisionAndFusion as StandDivisionAndFusionPlanningService,
  StandDivisionAndFusionFinalizeRequest,
} from './services/stand-division-and-fusion';
import { COMPLETE_STEPPER_MESSAGE, STANDDIVISIONANDFUSION_CONFIG } from './stand-division-and-fusion.config';
import {
  StepDefineAreaMaintenance,
  StepDefineAreaMaintenanceData,
} from './step-define-area-maintenance/step-define-area-maintenance';
import {
  StepDescriptionAndCompletion,
  StepDescriptionAndCompletionData,
} from './step-description-and-completion/step-description-and-completion';
import { StepSelectEstablishment } from './step-select-establishment/step-select-establishment';
import {
  StepSelectFusedStand,
  StepSelectFusedStandData,
} from './step-select-fused-stand/step-select-fused-stand';
import { StepSelectStandsToDivide } from './step-select-stands-to-divide/step-select-stands-to-divide';
import { extractBackendErrorMessage, isHandledByZnErrorHandler } from './utils/backend-error';

const STEP_COMPONENTS = [
  { id: 1, titleKey: 'StepSelectEstablishment.Title', component: StepSelectEstablishment },
  { id: 2, titleKey: 'StepSelectStandsToDivide.Title', component: StepSelectStandsToDivide },
  { id: 3, titleKey: 'StepDefineAreaMaintenance.Title', component: StepDefineAreaMaintenance },
  { id: 4, titleKey: 'StepSelectFusedStand.Title', component: StepSelectFusedStand },
  {
    id: 5,
    titleKey: 'StepDescriptionAndCompletion.Title',
    component: StepDescriptionAndCompletion,
  },
] as const;

@Component({
  selector: 'lib-stand-division-and-fusion',
  standalone: true,
  imports: [ZnStepperWizard, StepperSuccessModal],
  templateUrl: './stand-division-and-fusion.html',
})
export class StandDivisionAndFusion extends ZnWizardHost {
  private readonly completionModalBuilder = inject(ModalBuilder);
  private readonly finalizationDestroyRef = inject(DestroyRef);
  private readonly standDivisionAndFusionRouter = inject(Router);
  private readonly standDivisionAndFusionService = inject(StandDivisionAndFusionPlanningService);
  private readonly toaster = inject(ToasterService);

  protected readonly wizard = viewChild(ZnStepperWizard);
  protected readonly finalActionLabelKey = 'Geographic::StandDivisionAndFusion.FinalizeAction';

  private readonly finalizationInProgress = signal(false);

  constructor() {
    super();
    this.initializeStepperWizard(
      STANDDIVISIONANDFUSION_CONFIG.localizationKey,
      STEP_COMPONENTS,
      COMPLETE_STEPPER_MESSAGE,
      'Geographic::StandDivisionAndFusion.SuccessMessage',
    );
  }

  protected getWizard(): ZnStepperWizard | undefined {
    return this.wizard();
  }

  protected override onSubmitWizard(data: Record<number, unknown>): void {
    if (this.finalizationInProgress()) {
      return;
    }

    const finalizationInput = this.buildFinalizationInput(data);
    if (!finalizationInput) {
      return;
    }

    const modalRef = this.completionModalBuilder.openModal(
      COMPLETE_STEPPER_MESSAGE.component,
      COMPLETE_STEPPER_MESSAGE,
    );

    modalRef.closed
      .pipe(takeUntilDestroyed(this.finalizationDestroyRef))
      .subscribe((confirm: boolean) => {
        if (!confirm || this.finalizationInProgress()) {
          return;
        }

        this.finalizationInProgress.set(true);
        this.standDivisionAndFusionService
          .finalize(finalizationInput)
          .pipe(
            takeUntilDestroyed(this.finalizationDestroyRef),
            finalizeObservable(() => this.finalizationInProgress.set(false)),
          )
          .subscribe({
            next: () => {
              this.successModalVisible.set(true);
            },
            error: error => {
              this.handleFinalizationError(error);
            },
          });
      });
  }

  protected override async onSuccessModalVisibleChange(isVisible: boolean): Promise<void> {
    this.successModalVisible.set(isVisible);
    if (!isVisible) {
      await this.standDivisionAndFusionRouter.navigateByUrl('/geographic/stand');
    }
  }

  private buildFinalizationInput(
    data: Record<number, unknown>,
  ): StandDivisionAndFusionFinalizeRequest | null {
    const selection = data[2] as ISelectStandsToDivideStepData | undefined;
    const areaMaintenanceStep = data[3] as StepDefineAreaMaintenanceData | undefined;
    const fusedStandStep = data[4] as StepSelectFusedStandData | undefined;
    const completion = data[5] as StepDescriptionAndCompletionData | undefined;

    const description = completion?.transformationReason?.trim() ?? '';
    const maintenance = areaMaintenanceStep?.maintenance;
    const periodId = this.toPositiveInteger(maintenance?.period);
    const adjustmentReasonId = this.toPositiveInteger(maintenance?.reason);
    const standAreaMaintenanceTypeId = this.toPositiveInteger(maintenance?.adjustmentType);
    const selectedStands = this.mapSelectedStands(selection);
    const referenceCycleRotation = selection?.selectedStands[0]?.cycleRotation;
    const fusedStandId = this.toPositiveInteger(
      fusedStandStep?.fusedStandDetail?.id ?? fusedStandStep?.fusedStand?.id,
    );

    if (
      !description ||
      !periodId ||
      !adjustmentReasonId ||
      !standAreaMaintenanceTypeId ||
      !selectedStands ||
      !fusedStandId
    ) {
      return null;
    }

    return {
      description,
      areaMaintenance: {
        periodId,
        adjustmentReasonId,
        standAreaMaintenanceTypeId,
        notes: maintenance?.details?.trim() || null,
        isAreaUpdated: maintenance?.isAreaUpdated ?? false,
      },
      selectedStands,
      fusedStand: {
        standId: fusedStandId,
        landContractId: referenceCycleRotation?.landContractId ?? null,
        fomentContractId: referenceCycleRotation?.fomentContractId ?? null,
        propertyTypeId: referenceCycleRotation?.propertyTypeId ?? null,
      },
    };
  }

  private mapSelectedStands(
    selection: ISelectStandsToDivideStepData | undefined,
  ): StandDivisionAndFusionFinalizeRequest['selectedStands'] | null {
    const selectedStands = selection?.selectedStands ?? [];
    if (selectedStands.length < 2) {
      return null;
    }

    const mappedStands = selectedStands.map(row => ({
      standId: this.toPositiveInteger(row.id),
      newArea: this.toPositiveNumber(row.newArea),
    }));

    return mappedStands.every(row => !!row.standId && !!row.newArea) ? mappedStands : null;
  }

  private toPositiveInteger(value: string | number | null | undefined): number | null {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }

  private toPositiveNumber(value: number | null | undefined): number | null {
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
  }

  private handleFinalizationError(error: unknown): void {
    if (isHandledByZnErrorHandler(error)) {
      return;
    }

    console.error(error);

    const message =
      extractBackendErrorMessage(error) ??
      (error instanceof Error && error.message.trim() ? error.message : null);

    if (!message) {
      return;
    }

    this.toaster.error(message, '', { life: DEFAULT_TOASTER_LIFE });
  }
}

